import { useContext } from 'react';

import * as logger from '../../../logger';
import InputGroupContext from '../../../scenes/InputGroupContext';

import { Option } from '../../utils';
import { OptionEntry } from './OptionEntry';
import { OtherAutocompleteEntry } from './OtherAutocompleteEntry';

interface ExtendedMultiEnumProps<T> {
  otherOptions?: Option<T>[];
  onItemClick?: (value: T) => void;
  optionBadges?: BadgeValues[];
  onBadgeClick?: (value: string) => void;
  sortOptions?: boolean;
}

export interface BadgeValues {
  value: string;
  badges: string[];
}

interface Props<T> extends ExtendedMultiEnumProps<T> {
  options: Option<T>[];
  localizedOptions?: string[];
  values: T[];
  smallRowMargin?: boolean;
  ariaLabelledBy?: string;
  onChange?: (stringValues: string, values: T[]) => void;
}

const noneValues = [
  "I don't have a problem with these issues right now",
  "I don't have chronic conditions",
  'I have no concerns regarding my procedure at this time',
  'Never diagnosed',
  'Ninguna de las anteriores',
  'None of the above / Something else',
  'None of the above',
  'None of the below',
  'None of the following',
  'None of these',
];

export function MultiEnum<T>({
  options,
  otherOptions,
  values,
  optionBadges,
  onBadgeClick,
  smallRowMargin,
  sortOptions,
  localizedOptions,
  ariaLabelledBy,
  onItemClick,
  onChange,
}: Props<T>) {
  const inputGroup = useContext(InputGroupContext);

  if (!options) {
    logger.error(new Error('options are required with primitive MULTI_ENUM'));
    return null;
  }

  const updateOption = (option: Option<T>) => {
    const optionInValues = values.includes(option.value);
    const updatedValues: T[] = [];
    if (optionInValues) {
      updatedValues.push(...values.filter((value) => value !== option.value));
    } else if (noneValues.includes(option.display)) {
      updatedValues.push(option.value);
    } else {
      updatedValues.push(
        ...values.filter(
          (value) => typeof value !== 'string' || !noneValues.includes(value),
        ),
        option.value,
      );
    }
    if (onChange) onChange(JSON.stringify(updatedValues), updatedValues);
  };

  const onItemClickWithDefaultAction = (option: Option<T>) => {
    if (onItemClick) {
      onItemClick(option.value);
    } else {
      updateOption(option);
    }
  };

  const allOptions = [...options];
  if (otherOptions) {
    allOptions.push(
      ...otherOptions.filter((otherOption) =>
        values.includes(otherOption.value),
      ),
    );
  }
  if (sortOptions) {
    allOptions.sort((a, b) => a.display.localeCompare(b.display));
  }

  return (
    <div role="group" aria-labelledby={ariaLabelledBy || inputGroup}>
      {allOptions.map((option, i) => (
        <OptionEntry
          key={option.key}
          option={option}
          checked={values.includes(option.value)}
          onItemClick={onItemClickWithDefaultAction}
          smallRowMargin={smallRowMargin}
          translation={localizedOptions?.[i]}
          optionBadgeValues={optionBadges?.find(
            (badge) => badge.value === option.display,
          )}
          onBadgeClick={onBadgeClick}
        />
      ))}
      {!!otherOptions?.length && (
        <OtherAutocompleteEntry
          autoCompleteOptions={otherOptions}
          selectedValues={values}
          onItemClick={onItemClickWithDefaultAction}
        />
      )}
    </div>
  );
}
